/* ═══════════════════════════════════════════════════════
   CardStatusRail — the right-hand status column shared by the crew card and
   the client card sections. Presentational: everything is computed by the
   section and passed in. On phones it stacks under the content.
═══════════════════════════════════════════════════════ */
import React from "react";
import { C, mono, btnS, btnP, btnD, lbl } from "../../../../lib/styles.js";

const Chip = ({ state, label }) => {
  const color = state === "sent" ? C.green : state === "changed" ? C.purple : C.amber;
  return <span style={{ fontFamily: mono, fontSize: 10, letterSpacing: "0.06em", textTransform: "uppercase", padding: "3px 8px", borderRadius: 999, border: `1px solid ${color}66`, background: `${color}14`, color, whiteSpace: "nowrap" }}>{label}</span>;
};

export default function CardStatusRail({
  chip, filled, required, gaps = [], note, msg, busy,
  onPreview, onCopy, canCopy, onRevoke, onDeploy, deployLabel = "Deploy", deployDisabled,
  confirmRevoke, onConfirmRevoke, onCancelRevoke, revokeText,
  columns = [], rows = [], footer,
}) {
  const pct = Math.round((filled / Math.max(required, 1)) * 100);
  return (
    <div className="ccs-rail" style={{ border: `1px solid ${C.border}`, borderRadius: 6, background: C.surface, padding: 12, display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: 12, alignContent: "start", minWidth: 0, boxSizing: "border-box" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <Chip state={chip.state} label={chip.label} />
        {note && <span style={{ fontSize: 11, color: C.muted }}>{note}</span>}
      </div>

      <div>
        <div style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 12 }}>
          <span style={{ flex: 1, height: 5, background: C.surfaceAlt, borderRadius: 999, overflow: "hidden" }}>
            <span style={{ display: "block", height: "100%", width: `${pct}%`, background: gaps.length ? C.amber : C.green }} />
          </span>
          <span style={{ whiteSpace: "nowrap" }}>{filled} of {required}</span>
        </div>
        {gaps.length > 0 && (
          <div style={{ marginTop: 6, fontSize: 11.5, color: C.muted }}>
            Missing: <span style={{ color: C.amber, fontWeight: 500 }}>{gaps.map(g => g.label).join(" · ")}</span>
          </div>
        )}
      </div>

      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
        <button type="button" style={{ ...btnS, padding: "5px 10px", fontSize: 11 }} onClick={onPreview}>Preview</button>
        <button type="button" style={{ ...btnS, padding: "5px 10px", fontSize: 11, opacity: canCopy ? 1 : 0.4 }} disabled={!canCopy} onClick={onCopy}>Copy link</button>
        {onRevoke && <button type="button" style={{ ...btnD, padding: "5px 10px", fontSize: 11 }} onClick={onRevoke}>Revoke</button>}
        <button type="button" style={{ ...btnP, padding: "5px 12px", fontSize: 11, marginLeft: "auto", opacity: deployDisabled || busy ? 0.45 : 1 }} disabled={deployDisabled || busy} onClick={onDeploy}>{busy ? "Sending…" : deployLabel}</button>
      </div>

      {confirmRevoke && (
        <div style={{ display: "grid", gap: 6, padding: "8px 10px", background: `${C.amber}10`, border: `1px solid ${C.amber}40`, borderRadius: 6, fontSize: 12 }}>
          <span>{revokeText || "Revoke the link? Everyone who has it loses access immediately."}</span>
          <div style={{ display: "flex", gap: 6 }}>
            <button type="button" style={{ ...btnS, padding: "4px 10px", fontSize: 10, color: C.amber }} onClick={onConfirmRevoke}>Yes, revoke</button>
            <button type="button" style={{ ...btnS, padding: "4px 10px", fontSize: 10 }} onClick={onCancelRevoke}>Cancel</button>
          </div>
        </div>
      )}

      {msg && <div style={{ fontSize: 12, color: msg.ok ? (msg.warn ? C.amber : C.green) : C.danger, wordBreak: "break-word" }}>{msg.text}</div>}

      {rows.length > 0 && (
        <div className="ccs-tablewrap">
          <table className="ccs-table" style={{ width: "100%", borderCollapse: "collapse", fontSize: 11.5 }}>
            <thead><tr>{columns.map(h => <th key={h} style={{ textAlign: "left", fontFamily: mono, fontSize: 9, letterSpacing: "0.08em", textTransform: "uppercase", color: C.muted, fontWeight: 500, padding: "2px 6px 6px 0", borderBottom: `1px solid ${C.border}` }}>{h}</th>)}</tr></thead>
            <tbody>
              {rows.map((cells, i) => (
                <tr key={i}>
                  {cells.map((c, j) => (
                    <td key={j} style={{ padding: "5px 6px 5px 0", verticalAlign: "top", borderBottom: `1px solid ${C.border}33`, color: c.color || C.text, fontFamily: c.mono ? mono : undefined, fontSize: c.mono ? 10.5 : undefined, fontStyle: c.italic ? "italic" : undefined, whiteSpace: c.nowrap ? "nowrap" : undefined }}>{c.text}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {footer && <div style={{ ...lbl, textTransform: "none", letterSpacing: 0, fontSize: 11, color: C.muted, lineHeight: 1.5, overflowWrap: "anywhere", wordBreak: "break-word" }}>{footer}</div>}
    </div>
  );
}
